import React, {useEffect, useState} from 'react';
import {Button, Card, Select, Table, Tooltip} from 'antd';
import TooltipButton from "../TooltipButton/TooltipButton";
import moment from 'moment';
import GenericCreateUpdateModal from '../GenericCreateUpdateModal/GenericCreateUpdateModal';
import PlusOutlined from "@ant-design/icons/es/icons/PlusOutlined";
import EditTwoTone from "@ant-design/icons/es/icons/EditTwoTone";
import {useThemeSelector} from "../../redux/reducers/themeReducer";
import DeleteTwoTone from "@ant-design/icons/es/icons/DeleteTwoTone";
import DataType from "../../constants/DataType";
import InputDebounce from "../InputDebounce";
import GenericCrudPageContainer from "./GenericCrudPage.style";
import QuestionCircleOutlined from "@ant-design/icons/es/icons/QuestionCircleOutlined";
import GridActionIcon from "../GridActionIcon";
import EditOutlined from "@ant-design/icons/es/icons/EditOutlined";
import DeleteOutlined from "@ant-design/icons/es/icons/DeleteOutlined";
import useWindowSize from "../../helpers/useWindowSize";
import breakpoints from "../../constants/breakpoints";
import {useGenericCodebookSelector} from "../../redux/reducers/genericCodebookReducer";
import {useDispatch} from "react-redux";
import {
    getGenericCodebookData,
    onGenericCodebookDelete,
    resetGenericCodebookState,
    saveGenericCodebook,
    setFiltersAndFilterData,
    toggleGenericCodebookAddModal,
    toggleGenericCodebookEditModal,
    updateGenericCodebook
} from "../../redux/actions/genericCodebookActions";

const GenericCrudPage = props => {
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(getGenericCodebookData(props.linkFetch, props.columns));

        return (() => {
            dispatch(resetGenericCodebookState());
        })
    }, [props.linkFetch, props.columns]);

    const {primaryColor, darkMode} = useThemeSelector();
    const {
        data,
        isLoaded,
        filteredData,
        optionsMap,
        filtersMap,
        addModalVisible,
        submitPending,
        editItemInitialObject,
        editModalVisible
    } = useGenericCodebookSelector();
    const size = useWindowSize();

    const isDeviceSmall = size.width < breakpoints.sm;

    const [columnsDT, setColumnsDT] = useState([]);

    const onEdit = (record) => {
        dispatch(toggleGenericCodebookEditModal(true, record));
    };

    const onDelete = (record) => {
        dispatch(onGenericCodebookDelete(record, props.link));
    };

    const getActionsItems = (record) => {
        let {actionsEnabled, editAction, deleteAction, customActionsSimple, customActionsComplex} = props;
        const actionsItems = [];

        if (actionsEnabled) {
            if (editAction) {
                actionsItems.push(<GridActionIcon
                    key="edit"
                    title='Edit'
                    icon={<EditTwoTone
                        twoToneColor={primaryColor}/>}
                    darkIcon={<EditOutlined/>}
                    onClick={() => {
                        onEdit(record)
                    }}/>);
            }
            if (deleteAction) {
                actionsItems.push(<GridActionIcon
                    key="delete"
                    title='Delete'
                    icon={<DeleteTwoTone
                        twoToneColor={primaryColor}/>}
                    darkIcon={<DeleteOutlined/>}
                    onClick={() => {
                        onDelete(record)
                    }}/>);
            }
            if (customActionsSimple) {
                customActionsSimple.forEach(action => {
                    actionsItems.push(
                        (<TooltipButton size={'small'} key={action.title} icon={action.icon} title={action.title}
                                        onClick={() => action.onClick(record)}/>)
                    )
                })
            }
            if (customActionsComplex) {
                actionsItems.push(customActionsComplex);
            }
        }

        return actionsItems;
    };

    const updateFiltersMap = (column, type, value) => {
        dispatch(setFiltersAndFilterData(column, type, value))
    };

    useEffect(() => {
        let columns = [];
        ({columns} = props);

        let newColumns = [];

        columns.forEach(column => {
            if (!column.hideColumn) {
                if (column.type === DataType.BOOLEAN) {
                    column.render = (text, record, index) => {
                        let decideResult = column.decideFunction(record);
                        return decideResult[0] ?
                            (<div style={{
                                paddingTop: 1,
                                paddingBottom: 1,
                                borderRadius: 10,
                                width: 100,
                                textAlign: 'center',
                                backgroundColor: decideResult[2] ? decideResult[2] : 'green',
                                color: 'white',
                                display: "inline-block", ...column.style
                            }}> {decideResult[1]} </div>) :
                            (<div style={{
                                paddingTop: 1,
                                paddingBottom: 1,
                                borderRadius: 10,
                                width: 100,
                                textAlign: 'center',
                                backgroundColor: decideResult[2] ? decideResult[2] : 'red',
                                color: 'white', ...column.style
                            }}> {decideResult[1]} </div>);
                    }
                } else if (column.type === DataType.DATE || column.type === DataType.DATE_TIME) {
                    column.render = (text, record, index) => {
                        if (text != null)
                            return moment(text).format(column.format ? column.format : 'YYYY-MM-DD hh:ss:mm');
                        return '';
                    }
                } else if (column.type === 'colored-status') {
                    column.render = (text, record, index) => {
                        const [color, value] = column.decideFunction(record);
                        return (<div style={{
                            paddingTop: 1,
                            paddingBottom: 1,
                            borderRadius: 10,
                            width: 100,
                            textAlign: 'center',
                            backgroundColor: color,
                            color: 'white', ...column.style
                        }}> {value} </div>);
                    }
                } else if (column.type === DataType.SELECT) {
                    column.render = (text, record, index) => {

                        let optionsCol = optionsMap.find(optionsCol => {
                            return optionsCol.key === column.key;
                        });

                        let optionValueObj;
                        if (text != null && optionsCol) {
                            optionValueObj = optionsCol.options.find(option => {
                                return option.id === text;
                            });

                            return (
                                <span style={{...column.style}}>{optionValueObj ? optionValueObj.value : ''}</span>);
                        }

                        return "";
                    }
                } else {
                    column.render = (text, record, index) => {
                        return (<div>
                            <span style={{...column.style}}>{text}</span>
                        </div>)
                    }
                }
                newColumns.push(column);
            }
        });

        let actionCount = 0;
        if (props.editAction) {
            actionCount++;
        }
        if (props.deleteAction) {
            actionCount++;
        }
        if (props.customActionsSimple) {
            actionCount = actionCount + props.customActionsSimple.length;
        }
        if (props.customActionsComplex) {
            actionCount = actionCount + props.customActionsComplex.length;
        }

        let actionColumnWidth = actionCount * 55;

        let actions = {
            title: 'Actions',
            key: 'action',
            width: actionColumnWidth,
            render: (text, record) => (
                <span>
                    {getActionsItems(record)}
                </span>
            )
        };

        let preparedColumns = newColumns.map((column, index) => {
            const type = column.type || DataType.TEXT;
            let falseObj = {};
            let trueObj = {};
            falseObj[column.key] = false;
            trueObj[column.key] = true;

            let optionsCol;
            if (type === DataType.SELECT) {
                optionsCol = optionsMap.find(optionsCol => {
                    return optionsCol.key === column.key;
                })
            }

            return {
                title: (filters, sortOrder) => {
                    const value = filtersMap.find(key => column.key)?.value;

                    return (
                        <div>
                            <div><span>{column.title}</span></div>
                            {type === DataType.TEXT || type === DataType.INT || type === DataType.DOUBLE || type === DataType.DATE || type === DataType.DATE_TIME || type === DataType.TEXTAREA ? (
                                <div onClick={(e) => e.stopPropagation()} style={{paddingTop: 5}}>
                                    <InputDebounce allowClear={true} style={{width: '90%'}} onChange={(e) => {
                                        updateFiltersMap(column, type, e.target.value);
                                    }}
                                                   value={value}
                                    />
                                </div>) : null
                            }
                            {
                                type === DataType.BOOLEAN ? (
                                    <div onClick={(e) => e.stopPropagation()}>
                                        <Select
                                            showSearch
                                            style={{width: '100%'}}
                                            optionFilterProp="children"
                                            onChange={(e) => {
                                                updateFiltersMap(column, type, e);
                                            }}
                                            dropdownClassName={darkMode && 'dark-mode'}
                                        >
                                            <Select.Option
                                                value={column.decideFunction(falseObj)[1]}>{column.decideFunction(falseObj)[1]}</Select.Option>
                                            <Select.Option
                                                value={column.decideFunction(trueObj)[1]}>{column.decideFunction(trueObj)[1]}</Select.Option>
                                        </Select>
                                    </div>
                                ) : null
                            }
                            {
                                type === DataType.SELECT || type === DataType.MULTI_SELECT ? (
                                    <div onClick={(e) => e.stopPropagation()}>
                                        <Select
                                            allowClear
                                            showSearch
                                            style={{width: '100%'}}
                                            optionFilterProp="children"
                                            onChange={(e) => {
                                                updateFiltersMap(column, type, e);
                                            }}
                                            dropdownClassName={darkMode && 'dark-mode'}
                                        >
                                            {optionsCol ? optionsCol.options.map(option => {
                                                return (
                                                    <Select.Option key={option.id}
                                                                   value={option.id}>{option.value}</Select.Option>
                                                )
                                            }) : null}
                                        </Select>
                                    </div>
                                ) : null
                            }
                        </div>
                    )
                },
                dataIndex: column.dataIndex || column.key,
                key: column.key,
                filtered: true,
                filteredValue: [''],
                show: !(column.hideColumn && column.hideColumn === true),
                onFilter: (value, record) => {

                    const filter = filtersMap.find(item => {
                        return item.key === column.key;
                    });

                    if (filter) {
                        if (filter.type === DataType.TEXT || filter.type === DataType.TEXTAREA) {
                            if (record[column.key] && record[column.key].toLowerCase().includes(filter.value.toLowerCase())) {
                                return true;
                            } else {
                                return !record[column.key] && (!filter.value || filter.value === '');

                            }
                        } else if (filter.type === DataType.DATE || filter.type === DataType.DATE_TIME) {
                            if (filter.value.startsWith('>') || filter.value.startsWith('<')) {

                                let dateValue = filter.value.substr(1);

                                var re = new RegExp(/^\d{2}[./-//]\d{2}[./-///]\d{4}([./-///]?)$/);

                                if (re.test(dateValue.trim())) {
                                    const filterValue = moment(dateValue, 'DD-MM-YYYY');
                                    const fieldValue = moment(record[column.key]);

                                    if (filter.value.startsWith(">")) {
                                        return fieldValue.unix() - filterValue.unix() > 0;
                                    }

                                    if (filter.value.startsWith("<")) {
                                        return filterValue.unix() - fieldValue.unix() > 0;
                                    }
                                }
                            }
                        } else if (filter.type === DataType.BOOLEAN) {
                            return column.decideFunction(record).includes(filter.value);
                        } else if (filter.type === DataType.SELECT) {
                            return filter.value === record[column.key];

                        }
                    }
                    return true;
                },
                defaultSortOrder: 'descend',
                type,
                render: column.render,
                align: column.align ? column.align : 'left',
                width: column.width ? column.width : null,
                sorter: (a, b) => {
                    if (type === DataType.DATE || type === DataType.DATE_TIME) {
                        return moment(a[column.key]).unix() - moment(b[column.key]).unix()
                    } else {
                        if (a[column.key] < b[column.key]) //sort string ascending
                            return -1;
                        if (a[column.key] > b[column.key])
                            return 1;
                        return 0;
                    }
                }
            }
        });

        setColumnsDT([...preparedColumns, actions]);
    }, [isLoaded, data]);

    const rowSelection = {
        onChange: (selectedRowKeys, selectedRows) => {
            if (props.onRowSelectedChange) {
                props.onRowSelectedChange(selectedRowKeys, selectedRows);
            }
        },
        getCheckboxProps: record => ({
            disabled: props.disableRowForSelection ? props.disableRowForSelection(record) : null // Column configuration not to be checked
        }),
    };

    const openAddModal = () => {
        dispatch(toggleGenericCodebookAddModal(true));
    };

    const onOkAddModal = requestObj => {
        dispatch(saveGenericCodebook(requestObj, props.link));
    };

    const onCancelAddModal = () => {
        dispatch(toggleGenericCodebookAddModal(false));
    };

    const onOkEditModal = (requestObj) => {
        dispatch(updateGenericCodebook(requestObj, props.link));
    };

    const onCancelEditModal = () => {
        dispatch(toggleGenericCodebookEditModal(false));
    };

    const addDialogFields = props.columns.filter(column => {
        if (column.addField === undefined || column.addField === true)
            return {
                icon: column.icon,
                title: column.title,
                type: column.type,
                required: column.required,
                validationRules: column.validationRules
            }
    });

    const editDialogFields = props.columns.filter(column => {
        if (column.editField === undefined || column.editField === true)
            return {
                icon: column.icon,
                title: column.title,
                type: column.type,
                required: column.required,
            }
    });

    const renderTitle = () => {
        return props.helptip ? <>{props.title}<Tooltip title={props.helptip}><span
            className="codebook-helptip"><QuestionCircleOutlined/></span></Tooltip></> : props.title;
    };

    const getColumnsForSmallerDevice = () => {
        return [columnsDT[0], columnsDT[columnsDT.length - 1]];
    };

    return (
        <GenericCrudPageContainer darkMode={darkMode}>
            <Card title={renderTitle()}>
                <div style={{display: "flex", flexDirection: 'column', flex: 1}}>
                    <div className="basic-header">
                        <div style={{alignSelf: 'flex-end'}}>
                            {
                                props.additionalHeaderButtonsComplex ? (
                                    <div style={{display: 'inline-block'}}>
                                        {props.additionalHeaderButtonsComplex}
                                    </div>
                                ) : null
                            }
                            {
                                props.additionalHeaderButtonsSimple && props.additionalHeaderButtonsSimple.map(headerButton => {
                                    return (
                                        <Button style={{marginRight: 10}} className="basic-add-btn" type="primary"
                                                icon={headerButton.icon ? headerButton.icon : null} onClick={() => {
                                            if (headerButton.onClick)
                                                headerButton.onClick();
                                        }}>{headerButton.title}</Button>
                                    )
                                })
                            }
                            {(props.onAdd || props.addModal) &&
                            <Button className="basic-add-btn" type="primary" icon={<PlusOutlined/>} onClick={() => {
                                if (props.onAdd)
                                    props.onAdd();
                                else openAddModal();

                            }}>Add</Button>}
                        </div>
                    </div>
                    {addModalVisible && (props.addModal === undefined || props.addModal) ? (
                        <div><GenericCreateUpdateModal
                            title={"Add " + (props.title ? props.title : '')}
                            visible={addModalVisible}
                            onOk={onOkAddModal}
                            onCancel={onCancelAddModal}
                            submitPending={submitPending}
                            fields={addDialogFields}
                        >
                        </GenericCreateUpdateModal>
                        </div>) : null}
                    {editItemInitialObject != null && (props.editModal === undefined || props.editModal === true) ?
                        (
                            <GenericCreateUpdateModal
                                title={"Edit " + (props.title ? props.title : '')}
                                visible={editModalVisible}
                                onOk={onOkEditModal}
                                onCancel={onCancelEditModal}
                                submitPending={submitPending}
                                fields={editDialogFields}
                                initialValue={editItemInitialObject}>
                            </GenericCreateUpdateModal>
                        ) : null}
                    <div className="table-container">
                        {
                            (<Table
                                rowKey="id"
                                bordered={false}
                                loading={!isLoaded}
                                className="main-table"
                                scroll={{y: window.innerHeight - 280, scrollToFirstRowOnChange: true}} // TODO check
                                columns={isDeviceSmall ? getColumnsForSmallerDevice() : columnsDT}
                                dataSource={filteredData}
                                size="small"
                                expandRowByClick={props.expandRow || false}
                                expandedRowRender={props.expandRowRender || null}
                                rowSelection={props.rowSelectionEnabled ? rowSelection : false}
                                rowClassName={(record, index) => index % 2 === 0 ? 'table-row-light' : 'table-row-dark'}
                                pagination={{
                                    pageSizeOptions: ['10', '20', '50'],
                                    showSizeChanger: true, ...props.paginationProps
                                }}
                                onRow={(record, rowIndex) => {
                                    return {
                                        onClick: event => {
                                            if (props.onRowClick)
                                                props.onRowClick(record);
                                        }, // click row
                                        onDoubleClick: event => {
                                        }, // double click row
                                        onContextMenu: event => {
                                        }, // right button click row
                                        onMouseEnter: event => {
                                        }, // mouse enter row
                                        onMouseLeave: event => {
                                        }, // mouse leave row
                                    };
                                }}
                            />)
                        }
                    </div>
                </div>
            </Card>
        </GenericCrudPageContainer>
    );
};

export default GenericCrudPage;